import { buildFacet, buildNumericFacet } from '@coveo/headless';
import React, { memo, useEffect, useRef, useState } from 'react';
import { SET_SHOW_UNAVAILABLE_ITEMS } from '../../../aem-core-components/actions/constants';
import { useCartState } from '../../../contexts/cart';
import { useUserContext } from '../../../aem-core-components/context/UserContext';
import { VARIABLE_CONFIG } from '../../../constants/analyticsConstants/Variables';
import { ENV_CONFIG } from '../../../constants/envConfig';
import useCheckLocationEmpty from '../../../hooks/useCheckLocationEmpty';
import { useCheckAuthorityType } from '../../../hooks/useCheckUser';
import { usePricing } from '../../../hooks/usePricing';
import { useFilterState } from '../../cap';
import useFavourites from '../../favorites/hooks/useFavorites';
import { Tab, Tabs } from '../../global/atoms/tabs';
import { AUTHORITY_TYPE } from '../../global/constants';
import { isAtpCheckRequired, isNearByPcsRequired, triggerCoveoSearchAPI } from '../../global/utils/commonUtils';
import { rentals_engine, updateContextBasedOnCompanyId } from '../Engine';
import QuerySummaryHeader from '../components/facets/noResults/QuerySummaryHeader';
import {
    customSearchBox,
    querySummaryProductResultsController,
    querySummaryRelInfoController,
    relinfos_list,
    relinfos_searchbox_ctlr,
    rentals_list
} from '../controllers/searchresultscontroller';
import useSearchUtils from '../hooks/useSearchUtils';
import ProductResults from './ProductResults';
import RelatedResults from './RelatedResults';
import useAnalytics from '../../../hooks/useAnalytics';
import './searchResults.scss';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';

const SearchResults = () => {
    const [{ isProfileLoaded }] = useUserContext();
    const [{ cidPcList, isRatesLoading }] = useCartState();
    const { checkIfProductIsFavorite } = useFavourites();
    const [{ showUnavailableItems }, filterDispatch] = useFilterState();
    const { getProductPrices } = usePricing();
    const [{ payloadEcommerceViewItemList, handleAvailabilityToggleEvents }] = useAnalytics();
    const { sendEventsForViewItemList, sendEventsForUnavailableItem } = useAnalyticsContext();
    const [selTabIndex, setSelTabIndex] = useState(0);
    const [productState, setProductState] = useState();
    const [relatedState, setRelatedState] = useState();
    const [queryProductState, setProductQueryState] = useState();
    const [queryRentalState, setRentalQueryState] = useState();
    const [facetsList, setFacetsList] = useState([]);
    const [controllerConfig, setControllerConfig] = useState([]);
    const [{ initFacetsControllerList }] = useSearchUtils();
    const prevProductsRef = useRef([]);
    const { isRentalDetailsAvailable } = useCheckLocationEmpty();
    const authorityType = useCheckAuthorityType();
    const [isRateFetched, setIsRateFetched] = useState(false);
    const isProductsAvailable = productState?.results?.length > 0;

    const updateProductState = () => {
        setProductState(rentals_list.state);
    };

    const updateRelatedState = () => {
        setRelatedState(relinfos_list.state);
    };

    const updateProductQueryState = () => {
        setProductQueryState(querySummaryProductResultsController.state);
    };

    const updateRentalQueryState = () => {
        setRentalQueryState(querySummaryRelInfoController.state);
    };

    const productSkus = productState?.results?.map(item => item?.raw?.ec_skus[0]) || [];

    useEffect(() => {
        rentals_list.subscribe(updateProductState);
        relinfos_list.subscribe(updateRelatedState);
        querySummaryProductResultsController.subscribe(updateProductQueryState);
        querySummaryRelInfoController.subscribe(updateRentalQueryState);
    }, []);

    useEffect(() => {
        const globalheader = document.querySelector('.globalheader');
        const globalHeaderWrap = document.querySelector('header.experiencefragment');
        globalheader?.classList?.add('globalheader__search');
        globalHeaderWrap?.style?.setProperty('padding-top', `${globalheader.clientHeight}px`, 'important');
        globalHeaderWrap.style.boxShadow = 'none';
        getInitfacet();
    }, []);

    useEffect(() => {
        try {
            if (
                !queryProductState?.hasResults &&
                queryProductState?.firstSearchExecuted &&
                !queryProductState?.isLoading
            ) {
                ENV_CONFIG.POPULAR_CATEGORY_CONTAINER?.classList?.add('popular__categories--show');
                if (ENV_CONFIG.SRPShimmerContainer) {
                    ENV_CONFIG.SRPShimmerContainer.style.display = 'none';
                }
                ENV_CONFIG.SRPSearchContainer.style.display = 'block';
                if (ENV_CONFIG.SRPSeasonalCarousel) {
                    $('.seasonal__carousel')?.slick('refresh');
                }
            } else if (queryProductState?.hasResults && !queryProductState?.isLoading) {
                ENV_CONFIG.POPULAR_CATEGORY_CONTAINER?.classList?.remove('popular__categories--show');
                if (ENV_CONFIG.SRPShimmerContainer) {
                    ENV_CONFIG.SRPShimmerContainer.style.display = 'none';
                }
                ENV_CONFIG.SRPSearchContainer.style.display = 'block';
            } else {
                if (ENV_CONFIG.SRPShimmerContainer) {
                    ENV_CONFIG.SRPShimmerContainer.style.display = 'block';
                }
                ENV_CONFIG.SRPSearchContainer.style.display = 'none';
            }
        } catch (error) {
            if (ENV_CONFIG.SRPShimmerContainer) {
                ENV_CONFIG.SRPShimmerContainer.style.display = 'block';
            }
            ENV_CONFIG.SRPSearchContainer.style.display = 'none';
        }
    }, [queryProductState?.hasResults, queryProductState?.firstSearchExecuted, queryProductState?.isLoading]);

    useEffect(() => {
        prevProductsRef.current = [];
    }, [cidPcList]);

    useEffect(() => {
        if (isProfileLoaded) {
            if (isRentalDetailsAvailable() && cidPcList?.length > 0) {
                getPriceForProducts();
            }
        }
        if (authorityType === AUTHORITY_TYPE.P2P) {
            if (productSkus?.length > 0) {
                checkIfProductIsFavorite({
                    sku: productSkus,
                    pageSize: productSkus?.length
                });
            }
        }
    }, [JSON.stringify(productState?.results), cidPcList, isProfileLoaded]);

    useEffect(() => {
        if (isProductsAvailable) {
            if (!isRentalDetailsAvailable() || (!isRatesLoading && isRateFetched)) {
                sendEventsForViewItemList(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_VIEW_ITEM_LIST,
                    payloadEcommerceViewItemList(productState?.results)
                );
            }
        }
    }, [isRatesLoading, isRateFetched, isProductsAvailable]);

    useEffect(() => {
        if (!showUnavailableItems && isRateFetched) {
            sendEventsForUnavailableItem(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_INVENTORY_ALERT_VIEWED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_UNAVAILABLE_FROM_PC
            );
        }
    }, [showUnavailableItems, isRateFetched]);

    const shouldFetchRates = () => {
        const deltaProducts = productState?.results?.filter(item => {
            const { ec_skus } = item?.raw;
            if (prevProductsRef?.current?.indexOf(ec_skus[0]) === -1) {
                return true;
            }
            return false;
        });
        return deltaProducts;
    };

    const handleToggleChange = checked => {
        filterDispatch({ type: SET_SHOW_UNAVAILABLE_ITEMS, showUnavailableItems: checked });
        updateContextBasedOnCompanyId(
            [],
            [
                {
                    key: 'nearbypcs',
                    val: isAtpCheckRequired()
                }
            ],
            !checked
        );
        triggerCoveoSearchAPI();
        handleAvailabilityToggleEvents(checked);
        setIsRateFetched(false);
    };

    const getPriceForProducts = async () => {
        const deltaProducts = shouldFetchRates();
        if (deltaProducts?.length > 0) {
            getProductPrices(deltaProducts, VARIABLE_CONFIG.PAGE_TYPE.SEARCH_PAGE);
            prevProductsRef.current = productSkus;
        }
        setIsRateFetched(true);
    };

    useEffect(() => {
        updateContextBasedOnCompanyId(
            [],
            [
                {
                    key: 'nearbypcs',
                    val: isAtpCheckRequired()
                }
            ],
            isNearByPcsRequired(showUnavailableItems)
        );
    }, [queryProductState?.query]);

    const getInitfacet = async () => {
        const { facetsControllerList, data, controllerConfig } = await initFacetsControllerList(
            rentals_engine,
            buildFacet,
            buildNumericFacet,
            ''
        );
        setFacetsList(facetsControllerList);
        setControllerConfig(controllerConfig);
        const params = new URLSearchParams(window.location.search);
        const search = params.get('q');
        updateContextBasedOnCompanyId(
            [],
            [
                {
                    key: 'nearbypcs',
                    val: isAtpCheckRequired()
                }
            ],
            isNearByPcsRequired(false)
        );
        if (search) {
            customSearchBox?.submitFirstSearch(search);
            relinfos_searchbox_ctlr.updateText(search);
            relinfos_searchbox_ctlr.submit();
        } else {
            customSearchBox.submit();
            relinfos_searchbox_ctlr.submit();
        }
    };
    const searchCount = queryProductState && parseInt(queryProductState?.total);

    return (
        <>
            <section className={`block block--plp ${!searchCount && showUnavailableItems && 'no-search-result'}`}>
                {(queryProductState?.query !== '' || !showUnavailableItems) && (
                    <QuerySummaryHeader
                        searchTotalCount={searchCount}
                        term={queryProductState?.query}
                        showUnavailableItems={showUnavailableItems}
                    />
                )}
                {searchCount > 0 || showUnavailableItems ? (
                    <div className="container">
                        <Tabs selTabIndex={selTabIndex} onTabChange={setSelTabIndex}>
                            <Tab
                                title={`Rentals ${queryProductState && `(${queryProductState.total})`}`}
                                customButtonAriaLabel={
                                    selTabIndex === 0
                                        ? `Rentals ${queryProductState && `(${queryProductState.total})`
                                        }results selected `
                                        : `Rentals ${queryProductState && `(${queryProductState.total})`
                                        }results unselected `
                                }>
                                <ProductResults
                                    productState={productState}
                                    queryState={queryProductState}
                                    setQueryState={setProductQueryState}
                                    facetsList={facetsList}
                                    controllerConfig={controllerConfig}
                                    showUnavailableItems={showUnavailableItems}
                                    handleToggleChange={handleToggleChange}
                                    searchCount={searchCount}
                                    searchQuery={queryProductState?.query}
                                />
                            </Tab>
                            <Tab
                                title={`Related Information ${queryRentalState && `(${queryRentalState.total})`}`}
                                customButtonAriaLabel={
                                    selTabIndex === 0
                                        ? `Rentals ${queryProductState && `(${queryProductState.total})`
                                        }results selected `
                                        : `Rentals ${queryProductState && `(${queryProductState.total})`
                                        }results unselected `
                                }>
                                <RelatedResults
                                    relatedState={relatedState}
                                    queryState={queryRentalState}
                                    setQueryState={setRentalQueryState}
                                />
                            </Tab>
                        </Tabs>
                    </div>
                ) : null}
            </section>
        </>
    );
};

export default memo(SearchResults);
